// Demo bank accounts and statement lines. Matching follows the ledger's rules:
// a receipt or payment settles invoices or bills whose outstanding amounts add
// up exactly; a line is matched once; sign-off needs every line dealt with and
// the statement and ledger balances to agree.
import { addMoney, bicOk, compareMoney, formatMoney, ibanOk, isZero, negate, toUnits } from '@elysian/ui';
import { demoCashInBank, demoCashOnAccount, demoMarkCashInBank, demoRecordCash, demoStore } from './mockDocuments';
import type { BankTransactions, BankAccountSummary, BankDetails, BankFeedConnection, BankLine, BankRule, LedgerSource, MatchSuggestion, ReconciliationStatus } from './source';

type BankSource = Pick<LedgerSource, 'bankAccounts' | 'createBankAccount' | 'bankLines' | 'bankTransactions' | 'reconcileByHand' | 'untickByHand' | 'bankSuggestions' | 'reconcile' | 'unmatch' | 'reconciliation'
  | 'signOff' | 'bankRules' | 'createBankRule' | 'uploadStatement' | 'setBankDetails'
  | 'bankFeeds' | 'feedInstitutions' | 'connectBankFeed' | 'linkBankFeedAccount' | 'syncBankFeeds' | 'disconnectBankFeed'>;

const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));

interface Stored extends BankLine {
  account: string;
  settled?: { docId: string; amount: string }[];
  note?: string;
}

const OPENING: Record<string, string> = { '1200': '15822.39', '1210': '1300.00', '1220': '3150.00' };
/** What the books carry a foreign account at, in euro (the rates on the day each amount went in). */
const OPENING_BASE: Record<string, string> = { '1220': '3681.72' };
let lines: Stored[] = [];
let rules: BankRule[] = [];
let signedOff: Record<string, { as_at: string; by: string; at: string }> = {};
let seq = 0;
let details: Record<string, BankDetails> = {};
/** Entries reconciled by hand (#53): line id → statement date. */
let handMarks = new Map<string, string>();
let handLast: Record<string, string> = {};

// Demo open-banking feeds: connecting succeeds at once (no bank page), and the
// first "Sync now" on a linked account brings in a couple of new lines.
const FEED_BANKS: Record<string, { country: string; accounts: { name: string; iban: string; currency: string }[] }> = {
  AIB: { country: 'IE', accounts: [
    { name: 'Business Current', iban: 'IE29 •••• 5678', currency: 'EUR' },
    { name: 'Business Deposit', iban: 'IE64 •••• 5432', currency: 'EUR' }] },
  'Bank of Ireland': { country: 'IE', accounts: [{ name: 'Business Current', iban: 'IE12 •••• 4471', currency: 'EUR' }] },
  Revolut: { country: 'IE', accounts: [
    { name: 'EUR account', iban: 'IE87 •••• 0193', currency: 'EUR' },
    { name: 'GBP account', iban: 'GB33 •••• 7710', currency: 'GBP' }] },
};
let feeds: BankFeedConnection[] = [];
let feedSynced = new Set<string>();

function seed() {
  seq = 0;
  handMarks = new Map();
  handLast = {};
  feeds = [];
  feedSynced = new Set();
  details = { '1200': { account_name: 'Blaze Fire Systems Ltd', iban: 'IE29AIBK93115212345678', bic: 'AIBKIE2D', sort_code: null, account_number: null, is_default: true } };
  signedOff = { '1200': { as_at: '2026-09-30', by: 'ledger:user:brian', at: '2026-10-01T09:30:00Z' } };
  rules = [{ id: 'r1', name: 'AIB fees', match_text: 'AIB QUARTERLY FEE', direction: 'out', account_code: '7900', tax_code: 'EX', auto_apply: false }];
  const l = (txn_date: string, amount: string, description: string, extra: Partial<Stored> = {}): Stored =>
    ({ id: `k${++seq}`, account: '1200', txn_date, amount, description, reference: null, counterparty: null, status: 'unmatched', excluded_reason: null, ...extra });
  lines = [
    l('2026-10-09', '2800.00', 'BALLYMUN CREDIT UNION INV-1046'),
    l('2026-10-08', '-241.07', 'ELECTRIC IRELAND DD 55102934'),
    l('2026-10-07', '-12.50', 'AIB QUARTERLY FEE'),
    l('2026-10-06', '-500.00', 'TRANSFER TO DEPOSIT 1210'),
    l('2026-10-06', '1000.00', 'PARKWEST BUSINESS CAMPUS', { status: 'matched', note: 'INV-1045 (part)' }),
    l('2026-10-03', '-67.00', 'CIRCLE K NAAS 4471'),
    l('2026-10-02', '418.27', 'NAAS ROAD LOGISTICS LTD'),
    l('2026-09-30', '100.00', 'NAAS ROAD LOG', { status: 'matched', note: 'INV-1044 (part)' }),
    l('2026-09-25', '1112.30', 'DUBLIN HOTELS GROUP', { status: 'matched', note: 'INV-1048' }),
  ];
}
seed();

/** Unmatched lines up to a date, for the year-end checklist. */
export function unmatchedBankLines(upTo: string) {
  return lines.filter((l) => l.status === 'unmatched' && l.txn_date <= upTo).length;
}

/** Details printed for a design: its chosen account, else the default one (as ledger._printed_bank). */
export function printedBankFor(code?: string | null): (BankDetails & { code: string }) | null {
  const entry = Object.entries(details).find(([c, d]) => (code ? c === code : d.is_default));
  return entry ? { code: entry[0], ...entry[1] } : null;
}

/** Foreign-currency bank accounts with their balance and what the books carry it at, for revaluation. */
export function foreignBankBalances(asAt: string) {
  return demoStore.accounts.filter((a) => a.control === 'bank' && a.currency && a.currency !== 'EUR').map((a) => ({
    code: a.code, name: a.name, currency: a.currency!, amount: balances(a.code, asAt).ledger, carried: OPENING_BASE[a.code] ?? '0',
  }));
}

/** Checked by the demo's design save, as ledger.template_problems does. */
export function hasBankDetails(code: string): boolean {
  return code in details;
}

/** Set by the demo source: whether a saved design prints this account's details. */
let designPrints: (code: string) => boolean = () => false;
export function setDesignLookup(fn: (code: string) => boolean) {
  designPrints = fn;
}

/** Bank accounts added in the demo (#18), taken away again by resetDemoBank. */
let addedAccounts: string[] = [];

export function resetDemoBank() {
  seed();
  for (const code of addedAccounts) {
    const i = demoStore.accounts.findIndex((a) => a.code === code);
    if (i >= 0) demoStore.accounts.splice(i, 1);
  }
  addedAccounts = [];
  for (const a of demoStore.accounts) if (a.control === 'bank') a.is_active = true;
}

const contactName = (id: string) => demoStore.contacts.find((c) => c.id === id)?.name ?? '';

function suggestionsFor(b: Stored): MatchSuggestion[] {
  if (b.status !== 'unmatched') return [];
  const text = b.description.toLowerCase();
  const incoming = compareMoney(b.amount, '0') > 0;
  const amount = incoming ? b.amount : negate(b.amount);
  const out: MatchSuggestion[] = [];
  // Money already entered by hand into this account (#26): offered first.
  for (const c of demoCashInBank(b.account, b.amount)) {
    const named = text.includes(c.contact_name.split(' ')[0]!.toLowerCase()) || (c.reference && text.includes(c.reference.toLowerCase()));
    out.push({ kind: 'journal_lines', score: named ? 1 : 0.95, journal_line_ids: [`cash:${c.id}`],
      label: `Entered by hand: ${c.type === 'receipt' ? 'Receipt' : 'Payment'} ${c.number} — ${c.contact_name}${c.reference ? ` (${c.reference})` : ''} on ${new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${c.date}T12:00:00Z`))}` });
  }
  for (const d of demoStore.open()) {
    if (d.type !== (incoming ? 'sales_invoice' : 'purchase_bill') || compareMoney(d.outstanding, amount) !== 0) continue;
    const name = contactName(d.contact_id);
    const score = 0.6 + (d.doc_no && text.includes(d.doc_no.toLowerCase()) ? 0.3 : 0)
      + (text.includes(name.split(' ')[0]!.toLowerCase()) ? 0.15 : 0);
    out.push({ kind: 'documents', score: Math.min(score, 1), label: `${d.doc_no ?? d.supplier_ref ?? 'Bill'} · ${name}`, document_ids: [d.id] });
  }
  for (const r of rules) {
    const dirOk = r.direction === 'both' || (r.direction === 'in') === incoming;
    if (dirOk && text.includes(r.match_text.toLowerCase())) {
      const a = demoStore.accounts.find((x) => x.code === r.account_code);
      out.push({ kind: 'rule', score: 0.5, label: `Rule “${r.name}” → ${r.account_code} ${a?.name ?? ''}`.trim(), rule_id: r.id });
    }
  }
  return out.sort((a, b) => b.score - a.score);
}

function view(b: Stored): BankLine {
  const { account: _a, settled: _s, note: _n, ...line } = b;
  const best = suggestionsFor(b)[0];
  return { ...line, best_suggestion: best ?? null };
}

/** Lines that count towards the statement balance (excluded lines don't). */
function balances(code: string, asAt: string) {
  const mine = lines.filter((l) => l.account === code && l.txn_date <= asAt);
  const statement = addMoney(OPENING[code] ?? '0', ...mine.filter((l) => l.status !== 'excluded').map((l) => l.amount));
  const ledger = addMoney(OPENING[code] ?? '0', ...mine.filter((l) => l.status === 'matched').map((l) => l.amount));
  return { statement, ledger, unmatched: mine.filter((l) => l.status === 'unmatched').length };
}

function status(code: string, asAt: string): ReconciliationStatus {
  const b = balances(code, asAt);
  const s = signedOff[code]?.as_at === asAt ? signedOff[code] : undefined;
  return {
    as_at: asAt, ledger_balance: b.ledger, statement_balance: b.statement, unreconciled: b.unmatched,
    difference: addMoney(b.statement, negate(b.ledger)), signed_off_by: s?.by ?? null, signed_off_at: s?.at ?? null,
  };
}

function open(id: string): Stored | string {
  const b = lines.find((l) => l.id === id);
  if (!b) return 'That bank line no longer exists.';
  if (b.status !== 'unmatched') return `That bank line is already ${b.status}.`;
  return b;
}

function settle(b: Stored, note: string, settled?: Stored['settled']): Promise<BankLine> {
  lines = lines.map((l) => (l.id === b.id ? { ...l, status: 'matched', note, settled } : l));
  return delay(view(lines.find((l) => l.id === b.id)!));
}

const feedLines: Record<string, { txn_date: string; amount: string; description: string }[]> = {
  '1200': [
    { txn_date: '2026-10-10', amount: '214.20', description: 'STRIPE PAYOUT' },
    { txn_date: '2026-10-11', amount: '-142.28', description: 'EIR BUSINESS DD 0926' },
  ],
};

/** Why a demo bank account can't be switched off (#19), as the database says it; null when it can. */
export function bankAccountInUse(code: string): string | null {
  if (OPENING[code]) return 'it has transactions in the books';
  if (lines.some((l) => l.account === code)) return 'it has bank statement lines';
  if (feeds.some((f) => f.accounts.some((a) => a.account_code === code))) return 'a bank feed is linked to it; unlink the feed first';
  if (details[code]?.is_default) return 'its details are printed on invoices by default; make another account the default first';
  return null;
}

export const demoBank: BankSource = {
  createBankAccount: async (input) => {
    const name = input.name.trim();
    const bankAccounts = demoStore.accounts.filter((a) => a.control === 'bank');
    if (!name) return fail('Give the account a name');
    if (bankAccounts.some((a) => a.name.toLowerCase() === name.toLowerCase())) return fail(`There’s already a bank account called ${name}`);
    let code = input.code?.trim();
    if (code) {
      if (!/^\d{3,6}$/.test(code)) return fail('An account code is 3 to 6 digits');
      const taken = demoStore.accounts.find((a) => a.code === code);
      if (taken) return fail(`Code ${code} is already used by ${taken.name}`);
    } else {
      const last = Math.max(1190, ...bankAccounts.map((a) => Number(a.code)).filter((n) => n >= 1200 && n <= 1299));
      const free = (n: number) => !demoStore.accounts.some((a) => a.code === String(n));
      const tens = Array.from({ length: Math.max(0, Math.floor((1290 - (Math.floor(last / 10) + 1) * 10) / 10) + 1) }, (_, i) => (Math.floor(last / 10) + 1 + i) * 10);
      const n = tens.find(free) ?? Array.from({ length: 100 }, (_, i) => 1200 + i).find(free);
      if (n === undefined) return fail('Every code from 1200 to 1299 is taken; choose a code for the account');
      code = String(n);
    }
    const currency = (input.currency ?? 'EUR').toUpperCase();
    demoStore.accounts.push({ id: `a${code}`, code, name, class: 'asset', control: 'bank', is_active: true, currency: currency === 'EUR' ? null : currency });
    addedAccounts.push(code);
    if (input.details) {
      try { await demoBank.setBankDetails(code, input.details); } catch (e) {
        demoStore.accounts.splice(demoStore.accounts.findIndex((a) => a.code === code), 1);
        throw e;
      }
    }
    return (await demoBank.bankAccounts(true)).find((a) => a.code === code)!;
  },
  bankAccounts: (includeInactive = false) => {
    const today = '2026-12-31';
    return delay(demoStore.accounts.filter((a) => a.control === 'bank' && (includeInactive || a.is_active)).sort((a, b) => a.code.localeCompare(b.code)).map((a): BankAccountSummary => {
      const b = balances(a.code, today);
      const mine = lines.filter((l) => l.account === a.code);
      return {
        code: a.code, name: a.name, currency: a.currency ?? 'EUR', ledger_balance: b.ledger,
        base_balance: a.currency && a.currency !== 'EUR' ? OPENING_BASE[a.code] ?? b.ledger : b.ledger,
        statement_balance: mine.length ? b.statement : null,
        statement_date: mine.length ? mine.map((l) => l.txn_date).sort().at(-1)! : null,
        unmatched_count: b.unmatched, last_signed_off: signedOff[a.code]?.as_at ?? null,
        details: details[a.code] ?? null,
        ...(includeInactive ? { is_active: a.is_active, in_use: a.is_active && bankAccountInUse(a.code) !== null, in_use_reason: a.is_active ? bankAccountInUse(a.code) : null } : {}),
      };
    }));
  },
  bankFeeds: () => delay({ configured: true, connections: feeds }),
  feedInstitutions: () => delay(Object.entries(FEED_BANKS).map(([name, b]) => ({ name, country: b.country, psu_types: ['business', 'personal'],
    max_consent_days: 180, logo: null, preferred: true }))),
  connectBankFeed: (institution) => {
    const bank = FEED_BANKS[institution];
    if (!bank) return fail(`${institution} isn’t offered in Ireland.`);
    feeds = feeds.filter((f) => f.institution !== institution);
    const id = `feed-${institution.toLowerCase().replace(/\W+/g, '-')}`;
    feeds.push({
      id, provider: 'enable_banking', institution, country: bank.country, status: 'active', consent_expires_at: '2027-04-09T09:00:00Z',
      renew_soon: false, last_error: null, created_by: 'ledger:user:brian', created_at: '2026-10-11T09:00:00Z',
      accounts: bank.accounts.map((a, i) => ({ id: `${id}-${i}`, name: a.name, iban_masked: a.iban, currency: a.currency, account_code: null,
        account_name: null, last_synced_at: null, last_booked_date: null, last_balance: null, last_balance_date: null, last_error: null })),
    });
    // A real connection goes to the bank's consent page; the demo comes straight back as the bank would.
    return delay({ authorise_url: `/bank-accounts?feed=connected&message=${encodeURIComponent(`${institution}: ${bank.accounts.length} account(s) shared. Choose which bank account each one feeds.`)}` });
  },
  linkBankFeedAccount: (id, code) => {
    const account = feeds.flatMap((f) => f.accounts).find((a) => a.id === id);
    if (!account) return fail('That bank feed account no longer exists.');
    if (code) {
      const ledger = demoStore.accounts.find((a) => a.code === code && a.control === 'bank');
      if (!ledger) return fail(`${code} is not a bank account.`);
      const ccy = ledger.currency ?? 'EUR';
      if (ccy !== account.currency) return fail(`The bank account is in ${account.currency} but ${code} ${ledger.name} is kept in ${ccy}.`);
      if (feeds.some((f) => f.accounts.some((a) => a.account_code === code && a.id !== id))) return fail(`${code} ${ledger.name} already has a bank feed. Unlink that one first.`);
      account.account_code = code;
      account.account_name = ledger.name;
    } else {
      account.account_code = null;
      account.account_name = null;
    }
    return delay(feeds);
  },
  syncBankFeeds: () => {
    const results: { account_code: string; added: number; skipped: number }[] = [];
    for (const a of feeds.flatMap((f) => f.accounts)) {
      if (!a.account_code) continue;
      const fresh = feedSynced.has(a.account_code) ? [] : feedLines[a.account_code] ?? [];
      feedSynced.add(a.account_code);
      for (const n of fresh) lines.push({ id: `k${++seq}`, account: a.account_code, ...n, reference: null, counterparty: null, status: 'unmatched', excluded_reason: null });
      a.last_synced_at = '2026-10-11T09:05:00Z';
      if (fresh.length) a.last_booked_date = fresh.map((n) => n.txn_date).sort().at(-1)!;
      a.last_balance_date = a.last_booked_date ?? '2026-10-11';
      a.last_balance = Number(balances(a.account_code, a.last_balance_date).statement); // the bank's own figure agrees with its lines
      results.push({ account_code: a.account_code, added: fresh.length, skipped: 0 });
    }
    return delay({ results }, 200);
  },
  disconnectBankFeed: (id) => {
    feeds = feeds.filter((f) => f.id !== id);
    return delay(undefined);
  },
  // As ledger.bank_account_transactions (db/051): the demo's books are the opening balance, the statement lines
  // matched (reconciled) and receipts or payments entered by hand and not yet in the bank.
  bankTransactions: (code, q = {}) => {
    const a = demoStore.accounts.find((x) => x.code === code && x.control === 'bank');
    if (!a) return fail(`No bank account ${code}`);
    if (q.from && q.to && q.from > q.to) return fail('The start is after the end');
    type L = BankTransactions['lines'][number] & { amount: number };
    const all: L[] = [
      { id: `o-${code}`, date: '2026-01-01', journal_id: `jo-${code}`, journal_no: 'JNL-000001', source: 'opening_balance', description: 'Opening balances from Sage',
        contact: null, document: null, amount: Number(OPENING[code] ?? '0'), reconciled: null, reconciled_to: null },
      ...lines.filter((l) => l.account === code && l.status === 'matched').map((l): L => ({
        id: `m-${l.id}`, date: l.txn_date, journal_id: `j-${l.id}`, journal_no: `JNL-${l.id}`, source: Number(l.amount) > 0 ? 'receipt' : 'payment',
        description: l.note ?? l.description, contact: l.counterparty, document: null, amount: Number(l.amount), reconciled: true, reconciled_to: l.txn_date,
        reconciled_how: 'statement' })),
      ...demoCashOnAccount(code).filter((c) => !lines.some((l) => l.settled?.some((x) => x.docId === c.id))).map((c): L => ({
        id: `c-${c.id}`, date: c.date, journal_id: `jc-${c.id}`, journal_no: c.number, source: c.type, description: c.reference ?? (c.type === 'receipt' ? 'Receipt' : 'Payment'),
        contact: c.contact_name, document: { id: c.id, type: c.type, number: c.number }, amount: (c.type === 'receipt' ? 1 : -1) * Number(c.amount),
        reconciled: !!c.in_bank || handMarks.has(`c-${c.id}`), reconciled_to: handMarks.get(`c-${c.id}`) ?? null,
        reconciled_how: handMarks.has(`c-${c.id}`) ? 'hand' : c.in_bank ? 'statement' : null })),
    ].filter((l) => l.amount !== 0);
    const chrono = [...all].sort((x, y) => x.date.localeCompare(y.date) || x.journal_no.localeCompare(y.journal_no));
    let run = 0;
    const bal = new Map(chrono.map((l) => [l.id, (run = Math.round((run + l.amount) * 100) / 100)]));
    const before = chrono.filter((l) => q.from && l.date < q.from).reduce((t, l) => t + l.amount, 0);
    const inRange = chrono.filter((l) => (!q.from || l.date >= q.from) && (!q.to || l.date <= q.to));
    const shown = inRange.filter((l) => !q.unreconciled || l.reconciled === false).reverse();
    const page = shown.slice(q.offset ?? 0, (q.offset ?? 0) + Math.min(q.limit ?? 5000, 5000));
    const sum = (f: (l: L) => boolean) => Math.round(shown.filter(f).reduce((t, l) => t + Math.abs(l.amount), 0) * 100) / 100;
    return delay({
      account: { code, name: a.name, currency: a.currency ?? 'EUR', is_active: a.is_active },
      from: q.from ?? null, to: q.to ?? null, opening_balance: Math.round(before * 100) / 100, count: shown.length,
      money_in: sum((l) => l.amount > 0), money_out: sum((l) => l.amount < 0),
      closing_balance: inRange.length ? bal.get(inRange[inRange.length - 1]!.id)! : Math.round(before * 100) / 100,
      lines: page.map(({ amount, ...l }) => ({ ...l, money_in: amount > 0 ? amount : null, money_out: amount < 0 ? -amount : null,
        balance: q.unreconciled ? null : bal.get(l.id)! })),
      book_balance: run,
      reconciled_balance: Math.round(all.filter((l) => l.reconciled !== false).reduce((t, l) => t + l.amount, 0) * 100) / 100,
      last_reconciled: [...lines.filter((l) => l.account === code && l.status === 'matched').map((l) => l.txn_date), handLast[code] ?? ''].filter(Boolean).sort().at(-1) ?? null,
      unreconciled_count: all.filter((l) => l.reconciled === false).length,
      statement_lines_waiting: lines.filter((l) => l.account === code && l.status === 'unmatched').length,
    });
  },
  // As ledger.reconcile_by_hand (db/052): only entries not matched to a statement line, and only when the balance agrees.
  reconcileByHand: async (code, input) => {
    if (!input.statement_date) return fail('Give the statement’s date');
    if (!/^-?\d+(\.\d{1,2})?$/.test(input.closing_balance.trim())) return fail('Give the statement’s closing balance');
    const t = await demoBank.bankTransactions(code, {});
    const ticked = t.lines.filter((l) => input.journal_lines.includes(l.id));
    if (ticked.length !== input.journal_lines.length) return fail('Entry isn’t on this account');
    const bad = ticked.find((l) => l.reconciled !== false || l.date > input.statement_date);
    if (bad) return fail(`Entry ${bad.journal_no}: ${bad.reconciled_how === 'statement' ? 'is already matched to a statement line' : bad.reconciled ? 'is already reconciled by hand' : bad.reconciled === null ? 'is the opening balance, already counted' : 'is dated after the statement'}`);
    const after = Math.round((t.reconciled_balance + ticked.reduce((s, l) => s + (l.money_in ?? 0) - (l.money_out ?? 0), 0)) * 100) / 100;
    const close = Number(input.closing_balance);
    if (Math.abs(after - close) > 0.001) {
      return fail(`Out by ${(close - after).toFixed(2)}: the reconciled balance would be ${after.toFixed(2)}, but the statement says ${close.toFixed(2)}. Tick or untick entries until the difference is zero`);
    }
    for (const l of ticked) handMarks.set(l.id, input.statement_date);
    if (!handLast[code] || handLast[code]! < input.statement_date) handLast[code] = input.statement_date;
    return delay({ id: `hand${++seq}`, statement_date: input.statement_date, closing_balance: close, entries: ticked.length });
  },
  untickByHand: (_code, id, reason) => {
    if (reason.trim().length < 3) return fail('Give a reason (at least three characters)');
    if (!handMarks.delete(id)) return fail('That entry isn’t reconciled by hand');
    return delay(undefined);
  },
  bankLines: (code, st) => delay(lines.filter((l) => l.account === code && (!st || l.status === st))
    .sort((a, b) => b.txn_date.localeCompare(a.txn_date) || Number(b.id.slice(1)) - Number(a.id.slice(1))).map(view)),
  bankSuggestions: (id) => {
    const b = lines.find((l) => l.id === id);
    return b ? delay(suggestionsFor(b)) : fail('That bank line no longer exists.');
  },
  reconcile: (id, action) => {
    const b = open(id);
    if (typeof b === 'string') return fail(b);
    const amount = compareMoney(b.amount, '0') > 0 ? b.amount : negate(b.amount);
    switch (action.action) {
      case 'documents': {
        const docs = action.document_ids.map((d) => demoStore.get(d));
        if (docs.some((d) => !d)) return fail('One of those documents no longer exists.');
        const total = addMoney('0', ...docs.map((d) => d!.outstanding));
        if (compareMoney(total, amount) !== 0 && !action.fee_account_code) {
          return fail(`The documents come to ${formatMoney(total)} but the bank line is ${formatMoney(amount)}. Choose a fee account for the difference.`);
        }
        const settled = docs.map((d) => ({ docId: d!.id, amount: d!.outstanding }));
        settled.forEach((s) => demoStore.pay(s.docId, b.txn_date, s.amount));
        return settle(b, docs.map((d) => d!.doc_no ?? d!.supplier_ref).join(', '), settled);
      }
      case 'create': {
        const rule = action.rule_id ? rules.find((r) => r.id === action.rule_id) : undefined;
        const code = action.account_code ?? rule?.account_code;
        const acc = demoStore.accounts.find((a) => a.code === code);
        if (!acc) return fail('Choose an account for this transaction.');
        if (acc.control !== 'none') return fail(`${acc.code} ${acc.name} can’t be used here. Choose an income or expense account.`);
        return settle(b, `${acc.code} ${acc.name}`);
      }
      case 'transfer': {
        const other = demoStore.accounts.find((a) => a.code === action.other_account_code && a.control === 'bank');
        if (!other || other.code === b.account) return fail('Choose the other bank account.');
        return settle(b, `Transfer to ${other.name}`);
      }
      case 'exclude':
        if (action.reason.trim().length < 3) return fail('Give a reason (at least three characters).');
        lines = lines.map((l) => (l.id === b.id ? { ...l, status: 'excluded', excluded_reason: action.reason.trim() } : l));
        return delay(view(lines.find((l) => l.id === b.id)!));
      case 'match': {
        const ids = action.journal_line_ids.filter((x) => x.startsWith('cash:')).map((x) => x.slice(5));
        if (!ids.length) return fail('Nothing in the books matches this line yet.');
        ids.forEach(demoMarkCashInBank);
        return settle(b, 'Matched to money entered by hand');
      }
      case 'on_account': {
        const incoming = compareMoney(b.amount, '0') > 0;
        let left = Number(amount);
        const allocations = (action.document_ids ?? []).map((d) => demoStore.get(d)).filter(Boolean).map((d) => {
          const take = Math.min(left, Number(d!.outstanding));
          left = Math.round((left - take) * 100) / 100;
          return { document_id: d!.id, amount: take.toFixed(2) };
        }).filter((a) => Number(a.amount) > 0);
        return demoRecordCash(incoming ? 'receipt' : 'payment', { contact_id: action.contact_id, date: b.txn_date, amount: Number(amount).toFixed(2),
          bank_account: b.account, reference: b.description, allocations })
          .then((c) => { demoMarkCashInBank(c.id); return settle(b, `${c.number} — ${c.contact_name}, ${formatMoney(c.on_account)} on account`); });
      }
    }
  },
  unmatch: (id) => {
    const b = lines.find((l) => l.id === id);
    if (!b || b.status === 'unmatched') return fail('That line isn’t matched.');
    const s = signedOff[b.account];
    if (s && b.txn_date <= s.as_at) return fail(`This line is in a reconciliation signed off at ${s.as_at}; it can’t be unmatched.`);
    (b.settled ?? []).forEach((x) => demoStore.unpay(x.docId, x.amount));
    lines = lines.map((l) => (l.id === id ? { ...l, status: 'unmatched', excluded_reason: null, note: undefined, settled: undefined } : l));
    return delay(view(lines.find((l) => l.id === id)!));
  },
  reconciliation: (code, asAt) => delay(status(code, asAt)),
  signOff: (code, asAt) => {
    const s = status(code, asAt);
    if (s.unreconciled > 0 || !isZero(s.difference)) {
      return fail(`Not reconciled at ${asAt}: ${s.unreconciled} unmatched lines, statement ${formatMoney(s.statement_balance)} vs ledger ${formatMoney(s.ledger_balance)}.`);
    }
    signedOff[code] = { as_at: asAt, by: 'ledger:user:brian', at: new Date().toISOString() };
    return delay(status(code, asAt));
  },
  bankRules: () => delay(rules),
  createBankRule: (rule) => {
    if (!rule.match_text.trim()) return fail('Say what text to look for in the bank description.');
    if (!demoStore.accounts.some((a) => a.code === rule.account_code)) return fail(`Account ${rule.account_code} doesn’t exist.`);
    const saved: BankRule = { ...rule, id: `r${++seq}`, name: rule.name || rule.match_text, direction: rule.direction ?? 'both', auto_apply: false };
    rules = [...rules, saved];
    return delay(saved);
  },
  // Demo upload reads simple CSV: date,amount,description (a header row is skipped).
  uploadStatement: async (code, file) => {
    const text = await file.text();
    let imported = 0;
    let duplicates = 0;
    for (const row of text.split(/\r?\n/)) {
      const [date, amount, ...rest] = row.split(',');
      const description = rest.join(',').trim().replace(/^"|"$/g, '');
      if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date.trim()) || !amount) continue;
      try { toUnits(amount.trim()); } catch { continue; }
      if (lines.some((l) => l.account === code && l.txn_date === date.trim() && compareMoney(l.amount, amount.trim()) === 0 && l.description === description)) {
        duplicates++;
        continue;
      }
      lines.push({ id: `k${++seq}`, account: code, txn_date: date.trim(), amount: addMoney(amount.trim()), description, reference: null, counterparty: null, status: 'unmatched', excluded_reason: null });
      imported++;
    }
    if (imported + duplicates === 0) return fail('No transactions found. The demo reads CSV rows of date,amount,description.');
    return delay({ statement_id: `st${seq}`, imported, duplicates, auto_matched: 0 });
  },
  setBankDetails: async (code, input) => {
    const a = demoStore.accounts.find((x) => x.code === code);
    if (!a || a.control !== 'bank') return fail(`${code} isn’t a bank account`);
    const iban = input?.iban?.replace(/\s/g, '').toUpperCase() || null;
    const bic = input?.bic?.replace(/\s/g, '').toUpperCase() || null;
    const sort = input?.sort_code?.replace(/[\s-]/g, '') || null;
    const accNo = input?.account_number?.replace(/\s/g, '') || null;
    if (!iban && !sort && !accNo) {
      if (designPrints(code)) return fail(`An invoice or statement design prints ${code}’s details; choose another account there first`);
      delete details[code];
    } else {
      const name = input?.account_name?.trim();
      if (!name) return fail('Give the name the account is held in');
      if (iban && !ibanOk(iban)) return fail('That IBAN isn’t right; check it against a bank statement');
      if (bic && !bicOk(bic)) return fail('A BIC is 8 or 11 letters and digits (for example AIBKIE2D)');
      if (!iban && bic) return fail('A BIC goes with an IBAN');
      if (!sort !== !accNo) return fail('Give both the sort code and the account number');
      if (sort && !/^\d{6}$/.test(sort)) return fail('A sort code is six digits');
      if (accNo && !/^\d{8}$/.test(accNo)) return fail('A UK account number is eight digits');
      const wasDefault = details[code]?.is_default ?? false;
      const anyDefault = Object.values(details).some((d) => d.is_default);
      const makeDefault = input?.is_default ?? (wasDefault || !anyDefault);
      if (wasDefault && input?.is_default === false) return fail('Choose another account to print by default instead');
      if (makeDefault) for (const d of Object.values(details)) d.is_default = false;
      details[code] = { account_name: name, iban, bic, sort_code: sort, account_number: accNo, is_default: makeDefault };
    }
    const list = await demoBank.bankAccounts();
    return list.find((x) => x.code === code)!;
  },

};
